import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    AfterContentInit,
    ChangeDetectorRef,
    ContentChild,
    Directive,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2
} from '@angular/core';
import { NgControl, NgForm } from '@angular/forms';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import defaults from 'lodash-es/defaults';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import { Subscription } from 'rxjs';
import { FormFieldModel } from './form-field.model';

@Directive({})
export abstract class FormFieldAbstractComponent implements AfterContentInit, OnDestroy, OnChanges, OnInit {
    private type?: string;

    @Input()
    label?: string;

    @Input('sideIcon')
    _sideIcon?: 'string';

    @Input()
    hideSideIcon = false;

    @Input()
    busy = false;

    @Input()
    helper?: string;

    @Input()
    errors: { [error: string]: string } = {};

    @Input()
    errorOnType = false;

    @Input()
    errorMessage?: string;

    private _ngControl?: NgControl;

    @ContentChild(NgControl, { static: false })
    set ngControl(ngControl: NgControl | undefined) {
        this._ngControl = this.formFieldModel?.item?.ngControl || ngControl;
    }

    get ngControl(): NgControl | undefined {
        return this._ngControl;
    }

    private _inputRef?: ElementRef;

    @ContentChild(NgControl, { static: false, read: ElementRef })
    set inputRef(ref: ElementRef | undefined) {
        if (!ref) {
            this._inputRef = undefined;
            return;
        }
        this._inputRef = this.formFieldModel?.item?.inputRef || ref;
        const element = this._inputRef?.nativeElement as HTMLElement;
        const type = element?.tagName.toLowerCase();
        if (this.type) {
            this.renderer2.removeClass(this.elementRef.nativeElement, `_${this.type}`);
        }
        this.type = type ? (type === 'input' ? element.getAttribute('type') || type : type) : undefined;
        if (this.type) {
            this.renderer2.addClass(this.elementRef.nativeElement, `_${this.type}`);
        }
        // kill calendar opener for date field (in firefox`)
        if (this.type === 'date') {
            (this._inputRef.nativeElement as HTMLInputElement).addEventListener('click', e => {
                e.preventDefault();
            });
        }
    }

    get inputRef(): ElementRef | undefined {
        return this._inputRef || this.formFieldModel?.item?.inputRef;
    }

    @HostBinding('class._required')
    get isRequired(): boolean {
        const ngModelNativeElement = this.ngModelElementRef?.nativeElement;
        if (ngModelNativeElement) {
            return coerceBooleanProperty(ngModelNativeElement.required);
        }

        if (this.ngControl?.validator && this.ngControl.control) {
            const validationErrors = this.ngControl.validator(this.ngControl?.control);
            return validationErrors ? validationErrors['required'] : false;
        }
        return false;
    }

    @HostBinding('class._disabled')
    get isDisabled(): boolean {
        return this.ngControl?.disabled || false;
    }

    /**
     * If there was attempt to submit the form
     * only then we show validation errors
     */
    @HostBinding('class._submitted')
    get submitted(): boolean {
        return !!this.ngForm?.submitAttempt;
    }

    // responsible for _invalid class but cannot be hostBound cause of change detection
    private get isInvalid(): boolean {
        return !!this.ngControl?.errors;
    }

    // responsible for _isEmpty class but cannot be hostBound cause of change detection
    private get isEmpty(): boolean {
        return !!this.ngControl && !this.ngControl.value;
    }

    /**
     * Subscriptions list
     */
    private subscriptions = new Subscription();

    private initiated = false;

    /**
     * Dependencies
     */
    /**
     * Dependencies
     */
    constructor(
        private logger: Logger,
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private formFieldModel: FormFieldModel,
        private changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService,
        @Optional() private ngForm?: NgForm
    ) {
        this.subscriptions.add(
            this.formFieldModel.itemsChange.subscribe(item => {
                if (item) {
                    this.inputRef = item.inputRef;
                    this.ngControl = item.ngControl;
                    this.onNgControllChange();
                }
            })
        );
        this.subscriptions.add(
            this.formFieldModel?.paramsChange.subscribe(() => {
                this.detectChangesThrottled();
            })
        );
        if (this.ngForm) {
            this.subscriptions.add(this.ngForm.ngSubmit.subscribe(() => this.detectChanges()));
        }
    }

    ngAfterContentInit(): void {
        if (!this.ngControl) {
            this.logger.error(`Form component is missing ngModel inside (${this.label})`, this);
        } else {
            this.subscriptions.add(
                this.ngControl.valueChanges?.subscribe(change => {
                    this.detectChangesThrottled();
                })
            );
        }
        this.onNgControllChange();
    }

    ngOnInit() {
        this.prepareErrors();
        this.updateHostClasses();
    }

    ngOnChanges(changes) {
        if (changes.errors) {
            this.prepareErrors();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Retrieves error message
     */
    get validationMsg(): string | undefined {
        if (this.errorMessage) {
            return this.errorMessage;
        }
        const errorCodes = this.getErrors();
        if (errorCodes) {
            const code = Object.keys(this.errors).find(i => errorCodes.indexOf(i) > -1);
            return (code && this.errors[code]) || NOTICES[errorCodes[0]] || NOTICES['INVALIDFIELD'];
        }
        return undefined;
    }

    get sideIcon(): string {
        if (this.hideSideIcon || this.busy) {
            return '';
        }

        if (this._sideIcon) {
            return this._sideIcon;
        }
        switch (this.type) {
            case 'select':
                return 'icon-chevron-down';
            case 'text':
            case 'email':
            case 'textarea':
            case 'number':
                // case 'password': // you can uncomment if you move side text
                return (
                    !this.isDisabled &&
                    !this.isInvalid &&
                    this.ngControl &&
                    this.initiated &&
                    (this.value || this.value === 0) &&
                    'icon-tick col-3'
                );
            default:
                return '';
        }
    }

    get sideText(): string {
        if (this.busy) {
            return '';
        }
        switch (this.type) {
            case 'password':
                return this.inputRef?.nativeElement.type === 'password' ? 'show' : 'hide';
            default:
                return '';
        }
    }

    get isLoaderVisible(): boolean {
        return this.busy && !this.showsLoaderOnLabel();
    }

    get isLabelBusy(): boolean {
        return this.busy && this.showsLoaderOnLabel();
    }

    /**
     * Validation is shown after submission, but also when there are other errors then field not being filled1
     */
    get isValidationVisible(): boolean {
        if (this.errorMessage) {
            return true;
        }
        const errors = this.getErrors();
        return !!(errors && (this.submitted || (this.errorOnType && errors.find(i => i !== 'required'))));
    }

    sideAction() {
        switch (this.type) {
            case 'password':
                return this.toggleInputVisibility();
        }
    }

    private onNgControllChange() {
        if (!this.initiated && this.ngControl) {
            this.timeoutService.setTimeout(() => {
                this.initiated = true;
                this.detectChanges();
            }, 100);
            this.updateHostClasses();
            this.renderer2.addClass(this.elementRef.nativeElement, '_initiated');
        }
    }

    /**
     * Retrieves error codes from control
     */
    private getErrors(): string[] | null | undefined {
        const errors = this.ngControl?.errors;
        return errors && Object.keys(errors);
    }

    /**
     * Loader may appear as icon or as label busy state
     * the first does not work with checkboxes or radio buttons
     */
    private showsLoaderOnLabel(): boolean {
        if (this.type) {
            return ['checkbox', 'radio'].includes(this.type);
        }
        return false;
    }

    private prepareErrors() {
        this.errors = defaults(this.errors || {}, {
            email: NOTICES['INVLIDEMAILFIELD'],
            required: NOTICES['REQUIREDFIELD'],
            validateEqual: NOTICES['NOTMATCHED'],
            emojiDetected: NOTICES['EMOJIDETECTED']
        });
    }

    private toggleInputVisibility() {
        const input = this.inputRef?.nativeElement;
        if (input) {
            input.type = input.type === 'password' ? 'text' : 'password';
            input.type === 'password';
            this.detectChanges();
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private detectChangesThrottled = this.timeoutService.throttle(() => this.detectChanges(), 200, { leading: false, trailing: true });

    /**
     * Change detection
     */
    private detectChanges() {
        this.updateHostClasses();
        if (this.initiated) {
            this.changeDetectorRef.detectChanges();
        }
    }

    private updateHostClasses() {
        each(
            {
                _isEmpty: this.isEmpty,
                _invalid: this.isInvalid,
                _required: this.isRequired,
                _disabled: this.isDisabled
            },
            (value, className) => {
                value
                    ? this.renderer2.addClass(this.elementRef.nativeElement, className)
                    : this.renderer2.removeClass(this.elementRef.nativeElement, className);
            }
        );
    }

    /**
     * Value getter
     * needed in some cases
     */
    get value() {
        return this.ngControl?.value;
    }

    /**
     * Value setter
     * needed for calendar, normalizes input
     */
    set value(value) {
        if (this.ngControl) {
            if (value) {
                this.ngControl.control?.markAsDirty();
            }
            if (this.ngControl.control?.value !== value) {
                this.ngControl.control?.setValue(value);
            }
        }
    }

    get ngModelElementRef(): ElementRef | undefined {
        return this.ngControl?.valueAccessor?.['_elementRef'];
    }
}
